import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll } from "framer-motion";

const STEPS = [
  {
    index: "01",
    tag: "Brief",
    title: "A 60-minute kickoff — then silence.",
    body: "One call, one question: what do you want visitors to feel? We leave with a Figma-free, writing-first brief and a tight shot list. No endless Slack threads.",
    image:
      "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMGRhcmslMjBtb2RlJTIwbHV4dXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5Mzc5ODUzMnww&ixlib=rb-4.1.0&q=85",
  },
  {
    index: "02",
    tag: "Art direction",
    title: "One style frame. Then three.",
    body: "We design the single most important screen first, in full fidelity, before touching anything else. Nothing is built until a style frame makes the room go quiet.",
    image:
      "https://images.unsplash.com/photo-1677530410699-f692c94cf806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMGRhcmslMjBtb2RlJTIwbHV4dXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5Mzc5ODUzMnww&ixlib=rb-4.1.0&q=85",
  },
  {
    index: "03",
    tag: "Engineering",
    title: "Hand-written, zero-JS build.",
    body: "No page builders. No component libraries bolted on. Semantic HTML, tokenised CSS, and only the JavaScript the experience strictly requires.",
    image:
      "https://images.unsplash.com/photo-1630243237838-a44b1a87eb95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMGRhcmslMjBtb2RlJTIwbHV4dXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5Mzc5ODUzMnww&ixlib=rb-4.1.0&q=85",
  },
  {
    index: "04",
    tag: "Launch",
    title: "Ship it before the coffee cools.",
    body: "Edge deploys, analytics, SEO structured data, and a 30-minute walkthrough of the codebase. You own everything — repo, domain, deploy pipeline.",
    image:
      "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwd2Vic2l0ZSUyMGRlc2lnbiUyMGRhcmslMjBtb2RlJTIwbHV4dXJ5JTIwc3R1ZGlvfGVufDB8fHx8MTc5Mzc5ODUzMnww&ixlib=rb-4.1.0&q=85",
  },
];

const ProductShowcase = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const i = Math.min(STEPS.length - 1, Math.floor(v * STEPS.length));
      setActive(i);
    });
  }, [scrollYProgress]);

  return (
    <section
      id="showcase"
      ref={ref}
      data-testid="showcase-container"
      className="relative"
      style={{ height: `${STEPS.length * 95}vh` }}
    >
      <div className="sticky top-0 min-h-screen flex items-center overflow-hidden">
        <div className="max-w-[1280px] mx-auto w-full px-6 sm:px-10 py-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
          {/* Left — steps */}
          <div>
            <div className="mono-label mb-6">— 03 / Process</div>
            <h2 className="font-display font-light tracking-tight text-[clamp(2.2rem,4.5vw,4.2rem)] leading-[0.95] mb-10 max-w-[18ch]">
              From empty canvas
              <br />
              to <span className="font-editorial italic text-accent">live site</span> —
              <br />
              in four moves.
            </h2>

            <div className="flex flex-col gap-3">
              {STEPS.map((s, i) => (
                <div
                  key={s.index}
                  data-testid="showcase-step-item"
                  className={`relative flex gap-5 py-5 border-t transition-all duration-700 ${
                    i === active
                      ? "border-accent/30"
                      : "border-white/5"
                  }`}
                >
                  <div className={`font-mono text-[11px] tracking-[0.2em] pt-1 w-14 shrink-0 transition-colors duration-500 ${i === active ? "text-accent" : "text-[#5A5D68]"}`}>
                    {s.index}
                  </div>
                  <div className="flex-1">
                    <div className={`mono-label mb-1 transition-opacity duration-500 ${i === active ? "opacity-100" : "opacity-40"}`}>
                      {s.tag}
                    </div>
                    <h3 className={`font-display text-xl sm:text-2xl font-medium tracking-tight leading-snug mb-2 transition-colors duration-700 ${i === active ? "text-[#F4F4F2]" : "text-[#4A4D55]"}`}>
                      {s.title}
                    </h3>
                    <AnimatePresence>
                      {i === active && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.5 }}
                          className="text-[14px] leading-relaxed text-[#8A8D98] overflow-hidden"
                        >
                          {s.body}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — visual */}
          <div className="relative aspect-[4/5] w-full max-w-[560px] mx-auto">
            <div className="absolute inset-0 rounded-3xl border border-white/10 overflow-hidden glass-strong">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, scale: 1.08, filter: "blur(16px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.96, filter: "blur(8px)" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  <img
                    src={STEPS[active].image}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{
                      filter:
                        "grayscale(0.4) contrast(1.15) brightness(0.55) sepia(0.25) hue-rotate(-10deg)",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/50 to-[#050507]/30" />
                  <div className="absolute inset-0 mix-blend-color" style={{ background: "rgba(255,158,0,0.08)" }} />
                  {/* Spotlight */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 70% 25%, rgba(255,158,0,0.28), transparent 55%)",
                    }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 20% 90%, rgba(5,5,7,0.9), transparent 50%)",
                    }}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Framed overlay info */}
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4F4F2]/70">
                  {STEPS[active].index} / 04
                </div>
                <div className="flex gap-1.5">
                  {STEPS.map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: i === active ? 1 : 0.3, width: i === active ? 24 : 12 }}
                      transition={{ duration: 0.5 }}
                      className="h-px bg-[#F4F4F2]"
                    />
                  ))}
                </div>
              </div>

              <div className="absolute bottom-6 left-6 right-6">
                <div className="mono-label mb-1">{STEPS[active].tag}</div>
                <div className="font-display text-xl font-medium text-[#F4F4F2] max-w-[22ch] leading-tight">
                  {STEPS[active].title.replace(/\.$/, "")}
                </div>
              </div>
            </div>

            {/* Decorative off-frame badge */}
            <div className="absolute -top-6 -right-6 h-16 w-16 rounded-full glass flex items-center justify-center">
              <div className="font-mono text-[10px] text-accent tracking-[0.2em]">LIVE</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
